import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Modal, Text } from "src/components";
import { RichInput } from "src/components/richInput/richInput";
import { theme } from "src/config";
import { Item } from "src/models";
import { IItem } from "src/types";
import styled from "styled-components";
import { MenuBar, Section, SubView, SubViewBar } from ".";
import { Column, Row, Spacer } from "./gridElements";
import { StatusBar } from "./statusBar";
import { Tags } from "./tags";
import { TagView } from "./tagView";
import { TitleBar } from "./titleBar";


const Container = styled.div`
  max-width:      1280px;
  width:          64vw;
  height:         95vh;
  display:        flex;
  flex-direction: column;
  overflow-y:     hidden;
  @media (max-width: 1240px) {
    width:          90vw;
  }
`;

const ContentContainer = styled(Row)`
  padding:       1rem;
  overflow-y:    hidden;
  border-bottom: 1px solid ${theme.color.secondaryBorder};
  border-right:  1px solid ${theme.color.secondaryBorder};
  border-left:   1px solid ${theme.color.secondaryBorder};
`;

const LeftColumn = styled(Column)`
  width:         48vw;
  padding-right: 1rem;
  overflow-x:    hidden;

  @media (max-width: 1240px) {
    width:       70vw;
  }

  .rich-input {
    margin-bottom: 1rem;
  }
  > p {
    margin-bottom: 0.75rem;
  };
`;

const RightColumn = styled(Column)`
  width: 16vw;
  @media (max-width: 1240px) {
    width:          20vw;
  }
`;

export interface EditDataProps {
  data:       Item<IItem>,
  subViews:   SubView[],
  sections:   Section[],
  body?:      React.ReactNode,
  preTags?:   React.ReactNode,
  descLabel?: string,
  hideTags?:  boolean,
  canEdit?:   boolean,
  onClose():  void,
}

export const EditData = observer(React.forwardRef<{setActiveView: (view_key: string) => void}, EditDataProps>((props: EditDataProps, ref): JSX.Element => {
  const { data, onClose, subViews, sections, body, descLabel, hideTags, preTags, canEdit} = props;
  const { t, i18n }   = useTranslation();
  const subViewBarRef = React.useRef<HTMLElement & { setActive: (s: string) => void }>();
  React.useImperativeHandle(ref, () => ({
    setActiveView: (view_key: string): void => subViewBarRef?.current?.setActive(view_key)
  }));

  const _subViews: SubView[] = [
    {iconId: "tag", key: "tag", title: t("tag_plural"), body: <TagView item={data} />, hidden: true},
  ]

  if(!Boolean(data)) return null;

  return (
    <Modal noBorder show={Boolean(data)} onClose={onClose}>
      <Container>
        <TitleBar  {...props} />
        <StatusBar {...props} />
        <ContentContainer id="content_body" style={{opacity: data.isNew ? 0.2 : 1, pointerEvents: data.isNew ? 'none' : 'inherit' }}>
          <LeftColumn>
            <Text weight={"bold"} size="default">{descLabel || t("description")}</Text>
            <RichInput 
              editorProps   = {{editorStyle: {
                height:    "fit-content",
                minHeight: "15rem",
                overflow:  "auto",
                maxHeight: "30rem",
              }}}
              disabled      = {!canEdit}
              value         = {data.desc}
              onShowToolbar = {() => data.setEditProp("desc")}
              onHideToolbar = {() => data.setEditProp(null)}
              onSave        = {(text) => {
                data.set("desc", text, true);
                return data.desc;
              }}
              onDiscard = {() => {
                if(data.conflict) data.set("desc", data.conflict);
                data.setConflict(null);
                return data.desc;
              }}
            />
            {data.getEditProp === "desc" && data.conflict && <Text color="error">{t("desc_changed_in_db")}</Text>}
            {preTags}
            {!hideTags && <Tags item={data} onEditTags={() => subViewBarRef.current.setActive("tag")} />}
            <Spacer height="2.5rem"/>
            {body}
            <Spacer height="100%"/>
            <SubViewBar item={data} subViews={subViews.concat(_subViews)} ref={subViewBarRef}/>
          </LeftColumn>
          <RightColumn>
            <MenuBar sections={sections}/>
          </RightColumn>
        </ContentContainer>
      </Container>
    </Modal>
  );
}));